import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { trackCompleteRegistration } from "@/lib/fbpixel";
import AuthLayout, { SubmitBtn } from "./AuthLayout";

// Where a signed-out user was headed before login/verify interrupted them
// (e.g. an email "continue" link to /onboarding?job=<id>) — carried through
// from EmailLogin's redirect to this page.
function fromPath(location) {
  const from = location.state?.from;
  if (!from?.pathname) return null;
  return from.pathname + (from.search || "");
}

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
}

export default function VerifyOtp() {
  const nav = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const purpose = sp.get("purpose") || "verify"; // 'verify' | 'reset'
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(45);
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleChange = (i, v) => {
    const cleaned = (v || "").replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[i] = cleaned;
    setDigits(next);
    if (cleaned && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste.length === 6) {
      e.preventDefault();
      setDigits(paste.split(""));
      refs.current[5]?.focus();
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) {
      setErr("Please enter all 6 digits.");
      return;
    }
    if (purpose === "reset") {
      // For password reset, redirect to reset page with the code
      nav(`/reset-password?email=${encodeURIComponent(email)}&code=${code}`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/verify-otp", { email, code });
      // A verified account now exists — this is the true "registration complete"
      // moment (signup alone is unverified and can fail silently on email send).
      trackCompleteRegistration();
      toast.success("Email verified! Welcome.");
      await refreshAuth();
      nav(fromPath(location) || "/app/dashboard", { replace: true });
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/auth/resend-otp", { email, purpose });
      toast.success("New code sent.");
      setCooldown(45);
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    }
  };

  return (
    <AuthLayout
      title="Enter the 6-digit code"
      subtitle={`We sent it to ${email || "your email"}. It expires in 10 minutes.`}
    >
      <form onSubmit={submit} data-testid="verify-otp-form">
        <div className="flex justify-between gap-2 mb-4">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKey(i, e)}
              onPaste={handlePaste}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              data-testid={`otp-digit-${i}`}
              className="w-11 h-14 md:w-12 md:h-14 text-center rounded-md border border-lm-border bg-white text-2xl font-mono font-semibold text-lm-text focus:outline-none focus:border-lm-primary/50 focus:ring-2 focus:ring-lm-primary/10"
            />
          ))}
        </div>
        {err && <div className="mb-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div>}
        <SubmitBtn loading={busy} data-testid="verify-otp-submit">
          {purpose === "reset" ? "Continue" : "Verify email"}
        </SubmitBtn>
      </form>
      <div className="mt-5 text-center text-sm">
        <span className="text-lm-text3">Didn't get it? </span>
        <button
          onClick={resend}
          disabled={cooldown > 0}
          data-testid="verify-otp-resend"
          className="text-lm-primary hover:text-lm-primaryHover font-semibold underline underline-offset-4 disabled:opacity-50 disabled:no-underline"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </AuthLayout>
  );
}
